import { useCallback, useEffect, useRef, useState } from "react";
import { useCart } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { useNotification } from "../../../../hooks/useNotification";
import { diffProducts, type ProductInput } from "./utils/cartDiff";
import { deriveColorFromSchema } from "./utils/productHelpers";

interface UseCartSyncProps {
    selectedValues: any;
    materialSchema: any[];
    onValuesLoaded?: (values: any) => void;
    additionalProductsSelected?: any;
    onAdditionalProductsLoaded?: (products: any) => void;
}

/**
 * Hook to handle cart loading and syncing with selected values
 */
export function useCartSync({
    selectedValues,
    materialSchema,
    onValuesLoaded,
    additionalProductsSelected = {},
    onAdditionalProductsLoaded,
}: UseCartSyncProps) {
    const [changeInitiated, setChangeInitiated] = useState<boolean>(false);
    const isInitialLoad = useRef<boolean>(true);
    const onValuesLoadedRef = useRef(onValuesLoaded);
    const onAdditionalProductsLoadedRef = useRef(onAdditionalProductsLoaded);
    const { project } = useProject();
    const { showError } = useNotification();
    const { useCreate, useListByProjectId, useUpdate, useDelete } = useCart();

    const createCartSvc = useCreate();
    const listCartItemSvc = useListByProjectId();
    const updateCartSvc = useUpdate();
    const deleteCartSvc = useDelete();

    // Keep refs updated
    useEffect(() => {
        onValuesLoadedRef.current = onValuesLoaded;
    }, [onValuesLoaded]);

    useEffect(() => {
        onAdditionalProductsLoadedRef.current = onAdditionalProductsLoaded;
    }, [onAdditionalProductsLoaded]);

    /**
     * Loads initial cart items and populates selectedValues
     */
    const loadInitialCartItems = useCallback(async () => {
        if (!project?.id) {
            return;
        }

        isInitialLoad.current = true;
        setChangeInitiated(false);

        const response = await listCartItemSvc.mutateAsync({ ProjectId: project.id });
        const result: any = {};
        const additionalProducts: any = {};

        for (const item of response) {
            // Skip items without ProductCategory (shouldn't happen, but handle gracefully)
            if (!item.ProductCategory) {
                continue;
            }

            const base: any = {
                quantity: item.ItemCount,
                value: item.ProductName,
                manufacturerProductId: item.ManufacturerProductId,
            };

            // Include supplier info if available
            if (item.SupplierId) {
                base.supplierId = item.SupplierId;
            }
            if (item.SupplierName) {
                base.supplierName = item.SupplierName;
            }

            // Derive color from materialSchema if ManufacturerProductId exists
            const color = deriveColorFromSchema(materialSchema, item.ManufacturerProductId, item.ProductCategory);
            if (color) {
                base.color = color;
            }

            // Check if this is an additional product
            // Additional products have ProductCategory that doesn't match any category in materialSchema
            // and typically follows the pattern "ProductName_Category"
            const categoryExistsInSchema = materialSchema.some(
                (product: any) => (product.Name || product.ProductName || product.name) === item.ProductCategory
            );

            if (!categoryExistsInSchema && item.ProductCategory.includes("_")) {
                // This is likely an additional product (identifier format: "ProductName_Category")
                // Parse identifier: "ProductName_Category"
                const lastUnderscoreIndex = item.ProductCategory.lastIndexOf("_");
                const productName = item.ProductCategory.substring(0, lastUnderscoreIndex);
                const category = item.ProductCategory.substring(lastUnderscoreIndex + 1);

                additionalProducts[item.ProductCategory] = {
                    name: productName,
                    product: category,
                    quantity: item.ItemCount,
                };
            } else {
                result[item.ProductCategory] = base;
            }
        }

        if (onValuesLoadedRef.current) {
            onValuesLoadedRef.current(result);
        }

        if (onAdditionalProductsLoadedRef.current) {
            onAdditionalProductsLoadedRef.current(additionalProducts);
        }

        // Mark initial load as complete after state is set
        setTimeout(() => {
            isInitialLoad.current = false;
        }, 0);

        // Dispatch event to notify that cart items have been loaded/updated
        // This allows other components (like pricing) to refresh
        window.dispatchEvent(new Event("cartItemsLoaded"));
    }, [project?.id, materialSchema, listCartItemSvc]);

    /**
     * Syncs selectedValues and additionalProductsSelected with cart database
     */
    const syncCartWithDatabase = useCallback(async () => {
        try {
            if (!project?.id || isInitialLoad.current) {
                return;
            }

            // Note: changeInitiated check is now handled by the caller (useEffect in useMaterialsUpdate)
            // This allows the sync to proceed when explicitly called

            // Merge selectedValues and additionalProductsSelected into a single input
            // Include all items, even those with 0 quantity, so they can be properly deleted or updated
            const mergedInput: ProductInput = {};

            // Add all selectedValues (including those with 0 quantity)
            for (const [category, data] of Object.entries(selectedValues)) {
                if (data && typeof data === "object" && "value" in data) {
                    const itemData = data as {
                        quantity?: number;
                        value: string;
                        manufacturerProductId?: string;
                        color?: string;
                    };
                    // Only include items that have a value (product selected)
                    if (itemData.value) {
                        mergedInput[category] = {
                            quantity: itemData.quantity ?? 0,
                            value: itemData.value || "",
                            manufacturerProductId: itemData.manufacturerProductId,
                            color: itemData.color,
                        };
                    }
                }
            }

            // Convert additionalProductsSelected to ProductInput format
            for (const [identifier, product] of Object.entries(additionalProductsSelected || {})) {
                const { name, quantity } = product as any;
                mergedInput[identifier] = {
                    quantity: quantity ?? 0,
                    value: name,
                };
            }

            const response = await listCartItemSvc.mutateAsync({ ProjectId: project.id });
            const actions = diffProducts(response, mergedInput);

            // Only proceed if there are actual changes
            if (actions.toDelete.length === 0 && actions.toUpdate.length === 0 && actions.toCreate.length === 0) {
                // If there are selected items with 0 quantity, surface a warning so the user understands
                const zeroQuantityCategories = Object.entries(mergedInput)
                    .filter(([_, item]) => (item as any).value && ((item as any).quantity ?? 0) === 0)
                    .map(([category]) => category);

                if (zeroQuantityCategories.length > 0) {
                    showError(
                        `Cart update skipped because quantity is 0 for: ${zeroQuantityCategories.join(
                            ", "
                        )}. Increase the quantity to add these items to the cart.`
                    );
                }
                setChangeInitiated(false);
                return;
            }

            // Delete items
            if (actions.toDelete.length > 0) {
                for (const itemToDelete of actions.toDelete) {
                    await deleteCartSvc.mutateAsync({ Id: itemToDelete.Id });
                }
            }

            // Update items
            if (actions.toUpdate.length > 0) {
                for (const item of actions.toUpdate) {
                    await updateCartSvc.mutateAsync({ Id: item.Id, ItemCount: item.ItemCount });
                }
            }

            // Create items
            if (actions.toCreate.length > 0) {
                await createCartSvc.mutateAsync({
                    ProjectId: project.id,
                    CartItems: actions.toCreate,
                });
            }

            setChangeInitiated(false);
        } catch (e) {
            console.error("Error syncing cart:", e);
            setChangeInitiated(false);
        }
    }, [
        project?.id,
        selectedValues,
        additionalProductsSelected,
        changeInitiated,
        listCartItemSvc,
        deleteCartSvc,
        updateCartSvc,
        createCartSvc,
        showError,
    ]);

    return {
        changeInitiated,
        setChangeInitiated,
        loadInitialCartItems,
        syncCartWithDatabase,
        isInitialLoadRef: isInitialLoad,
    };
}
